import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Toolbar,
  Filter,
} from '@syncfusion/ej2-react-grids';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { SalesTaxEdit } from './SalesTaxEdit';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { FormButtonsContainer } from '@components/Container';
import { CancelButton } from '@components/Form';
import { OkButton } from '@components/Form/OkButton';
import { FilterRecord, TableActions } from '@components/Grid';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import { useGridFilters } from '@hooks/useGridFilters';
import { useGridOperations } from '@hooks/useGridOperations';
import { useNotification } from '@hooks/useNotification';
import { useSyncfusionGrid } from '@hooks/useSyncfusionGrid';
import { SalesTaxService } from '@services/SalesTax';

type SalesTaxContextType = {
  captureRefreshFunction: (refreshFn: () => void) => void;
};

export const SalesTaxList: React.FC = () => {
  const { captureRefreshFunction } = useOutletContext<SalesTaxContextType>();
  const [showEditSalesTax, setShowEditSalesTax] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedSalesTaxId, setSelectedSalesTaxId] = useState<number>(0);
  const [salesTaxName, setSalesTaxName] = useState<string>('');
  const [queryFilter, setQueryFilter] = useState<string>('');
  const { showError } = useNotification();

  // Create a service wrapper that matches useSyncfusionGrid interface
  const salesTaxServiceWrapper = {
    ...SalesTaxService,
    getList: async (
      page: number,
      take: number | undefined,
      sortBy: string | undefined,
      sortDirection: string | undefined,
      filters: string
    ) => {
      return SalesTaxService.getList(
        page,
        take,
        undefined,
        undefined,
        filters,
        sortBy,
        sortDirection
      );
    },
  };

  // Custom hooks for grid functionality
  const { filters, updateFilter, clearFilters, applyFilters } = useGridFilters(
    { nameFilter: '' },
    setQueryFilter
  );

  const {
    gridData,
    gridRef,
    gridKey,
    renderComplete,
    dataStateChange,
    fetchData,
    editOptions,
  } = useSyncfusionGrid({
    service: salesTaxServiceWrapper,
    queryFilter,
    defaultSortBy: 'ID',
    defaultSortDirection: 'desc',
  });

  const { handleDelete: handleDeleteOperation } = useGridOperations({
    service: SalesTaxService,
    refreshGrid: fetchData,
    entityName: 'sales tax',
  });

  useEffect(() => {
    captureRefreshFunction(fetchData);
  }, [captureRefreshFunction, fetchData]);

  const handleEdit = (rowId: any) => {
    setSelectedSalesTaxId(rowId);
    setShowEditSalesTax(true);
  };

  const handleDelete = async (rowId: number, rowSalesTaxName: string) => {
    try {
      setSelectedSalesTaxId(rowId);
      setSalesTaxName(rowSalesTaxName);
      setShowDelete(true);
    } catch (error) {
      const errorMessage = `Failed to handle delete action for sales tax ${rowSalesTaxName}.`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const confirmDelete = () => {
    handleDeleteOperation(selectedSalesTaxId, salesTaxName);
    setShowDelete(false);
  };

  const rateTemplate = (props: any) => {
    const rateValue = props.rate;
    return rateValue !== null && rateValue !== undefined ? (
      <div>{rateValue} %</div>
    ) : null;
  };

  const actionButtons = (props: any) => {
    return (
      <TableActions
        dropdownActions={[
          {
            title: 'Edit',
            onClick: () => handleEdit(props.id),
          },
          {
            showTopSeparator: true,
            title: 'Delete',
            onClick: () => handleDelete(props.id, props.name),
          },
        ]}
      />
    );
  };

  return (
    <>
      <FilterRecord
        label="Filter by Name"
        value={filters.nameFilter}
        onChange={(e) => updateFilter('nameFilter', e.target.value)}
        onFilter={() => {
          applyFilters(() => fetchData(true));
        }}
        onClear={() => {
          clearFilters();
          applyFilters(() => fetchData(true));
        }}
      />

      <Space />

      <GridComponent
        key={gridKey}
        dataBound={renderComplete}
        dataSource={gridData}
        ref={gridRef}
        allowPaging={true}
        pageSettings={{
          pageSize: 10,
          pageSizes: [5, 10, 20, 50, 100],
        }}
        editSettings={editOptions}
        dataStateChange={dataStateChange}
      >
        <ColumnsDirective>
          <ColumnDirective field="name" headerText="Tax Name" />
          <ColumnDirective field="description" headerText="Description" />
          <ColumnDirective field="abbreviation" headerText="Abbreviation" />
          <ColumnDirective template={rateTemplate} headerText="Tax Rate" />
          <ColumnDirective
            headerText="Actions"
            width={100}
            template={actionButtons}
          />
        </ColumnsDirective>
        <Inject services={[Page, Toolbar, Filter]} />
      </GridComponent>

      {showEditSalesTax && (
        <Modal show={true}>
          {selectedSalesTaxId && (
            <SalesTaxEdit
              taxId={selectedSalesTaxId}
              onClose={(refreshGrid?: boolean) => {
                setShowEditSalesTax(false);

                if (refreshGrid) {
                  fetchData(true);
                }
              }}
              onDelete={() => {
                setShowEditSalesTax(false);
                handleDelete(selectedSalesTaxId, salesTaxName);
              }}
            />
          )}
        </Modal>
      )}

      {showDelete && (
        <Modal show={true}>
          <CloseButton onClick={() => setShowDelete(false)} />
          <Box size="medium">
            <div className="px-4 pb-2">
              <span>Are you sure to delete Sales Tax: {salesTaxName}</span>
            </div>
            <FormButtonsContainer>
              <div className="space-x-2">
                <CancelButton onClick={() => setShowDelete(false)} />
                <OkButton onClick={confirmDelete}>Delete</OkButton>
              </div>
            </FormButtonsContainer>
          </Box>
        </Modal>
      )}
    </>
  );
};
